// Form
import {
    FieldValidator,
    FieldValidatorResult,
    composeValidators,
    isRequired,
    isValidTime,
    isTime,
    isDateMixed,
    isNumber
} from 'forms/validators';
import { WorkType } from 'services/workTypes/types';
import { Person } from 'services/people/types';
import { EntityPathNames } from 'services/entities/types';

import { TimesheetCSVEntry } from './types';

const timesheetEntryValidators: { [T in keyof TimesheetCSVEntry]?: any } = {
    date: composeValidators(isRequired, isDateMixed),
    startTime: composeValidators(isRequired, isTime, isValidTime),
    endTime: composeValidators(isRequired, isTime, isValidTime),
    personReference: composeValidators(isRequired, isNumber),
    taskName: isRequired,
    area: isRequired,
    rate: isNumber,
    units: isNumber
};

export const makeCsvEntriesValidator =
    (
        workTypesKeyedByName: Record<string, WorkType>,
        peopleKeyedByDatacomId: Record<string, Person>,
        entityPathName: Record<string, EntityPathNames>
    ): FieldValidator =>
    (entries?: Partial<TimesheetCSVEntry>[]): FieldValidatorResult => {
        if (!entries) {
            return 'Required';
        }

        for (let i = 0; i < entries.length; i++) {
            const entry = entries[i];
            const { taskName, personReference, area } = entry;

            // Per-attribute validation
            for (const key of Object.keys(timesheetEntryValidators)) {
                const validator =
                    timesheetEntryValidators[key as keyof TimesheetCSVEntry];

                if (
                    validator &&
                    validator(entry[key as keyof TimesheetCSVEntry])
                ) {
                    const error = validator(
                        entry[key as keyof TimesheetCSVEntry]
                    );

                    return `Line ${i + 1}: ${key}: ${error}`;
                }
            }

            // WorkType validation
            if (!workTypesKeyedByName[taskName as string]) {
                return `Line ${i + 1}: Invalid taskName ${taskName}`;
            }

            // Person validation
            if (!peopleKeyedByDatacomId[personReference as string]) {
                return `Line ${
                    i + 1
                }: Invalid personReference ${personReference}`;
            }

            // Area validation
            if (!entityPathName[area as string]) {
                return `Line ${i + 1}: Invalid area ${area}`;
            }
        }

        if (!entries.length) return 'Requires at least 1 header and 1 line';

        return undefined;
    };
